import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { ArrowUpRight, BookOpen, Users, DollarSign, Award, GraduationCap, Sparkles } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [courses, setCourses] = useState([]);
  const [enrollments, setEnrollments] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [a, c, e] = await Promise.all([
          api.get("/analytics/overview"),
          api.get("/courses"),
          api.get("/enrollments/mine"),
        ]);
        setStats(a.data);
        setCourses(c.data);
        setEnrollments(e.data);
      } catch {}
    })();
  }, []);

  const isCreator = user?.role === "instructor" || user?.role === "org_admin";

  return (
    <Layout>
      <div className="px-10 py-10 max-w-7xl">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="overline mb-3">{new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</div>
            <h1 className="editorial-h1 text-5xl">Welcome back, {user?.name?.split(" ")[0]}.</h1>
          </div>
          {isCreator && (
            <Link to="/builder/new" data-testid="dashboard-new-course" className="btn-primary">
              New course <ArrowUpRight size={16} strokeWidth={1.5} />
            </Link>
          )}
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
          {isCreator ? (
            <>
              <StatCard label="Courses" value={stats?.courses ?? 0} icon={BookOpen} testid="stat-courses" />
              <StatCard label="Learners" value={stats?.learners ?? 0} icon={Users} testid="stat-learners" />
              <StatCard label="Revenue" value={`$${(stats?.revenue ?? 0).toFixed(0)}`} icon={DollarSign} testid="stat-revenue" accent />
              <StatCard label="Completed" value={stats?.completed ?? 0} icon={Award} testid="stat-completed" />
            </>
          ) : (
            <>
              <StatCard label="Enrolled" value={stats?.enrollments ?? 0} icon={GraduationCap} testid="stat-enrolled" />
              <StatCard label="Completed" value={stats?.completed ?? 0} icon={BookOpen} testid="stat-completed" />
              <StatCard label="Certificates" value={stats?.certificates ?? 0} icon={Award} testid="stat-certs" accent />
              <StatCard label="In progress" value={(stats?.enrollments ?? 0) - (stats?.completed ?? 0)} icon={Sparkles} testid="stat-in-progress" />
            </>
          )}
        </div>

        {/* Continue learning */}
        {enrollments.length > 0 && (
          <section className="mb-14">
            <div className="flex items-end justify-between mb-6">
              <h2 className="editorial-h2 text-3xl">Continue learning</h2>
              <Link to="/learning" className="overline hover:text-[#E63946]">All →</Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {enrollments.slice(0, 3).map((e) => (
                <Link key={e.enrollment_id} to={`/learn/${e.course_id}`} data-testid={`enrollment-card-${e.course_id}`} className="editorial-card p-6 block">
                  {e.course?.thumbnail && <img src={e.course.thumbnail} className="w-full h-32 object-cover mb-4 border border-[#E4E4E7]" alt="" />}
                  <div className="overline mb-2">{e.course?.category || "Course"}</div>
                  <div className="font-serif text-2xl leading-tight mb-4">{e.course?.title || "Course"}</div>
                  <div className="h-1 bg-[#E4E4E7] mb-2"><div className="h-1 bg-[#0A0A0A]" style={{ width: `${e.progress_pct}%` }} /></div>
                  <div className="overline">{e.progress_pct}% complete</div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Recent courses */}
        <section>
          <div className="flex items-end justify-between mb-6">
            <h2 className="editorial-h2 text-3xl">{isCreator ? "Your catalog" : "Available courses"}</h2>
            <Link to="/courses" className="overline hover:text-[#E63946]">View all →</Link>
          </div>
          {courses.length === 0 ? (
            <div className="editorial-card-static p-12 text-center">
              <div className="font-serif text-2xl mb-2">No courses yet</div>
              <div className="text-[#52525B] mb-6">{isCreator ? "Build your first course in under 90 seconds with AI." : "Browse the marketplace to enroll."}</div>
              {isCreator ? (
                <Link to="/builder/new" className="btn-primary" data-testid="empty-create-course">Create course</Link>
              ) : (
                <Link to="/marketplace" className="btn-primary" data-testid="empty-browse">Browse marketplace</Link>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {courses.slice(0, 6).map((c) => (
                <CourseCard key={c.course_id} course={c} />
              ))}
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
}

function StatCard({ label, value, icon: Icon, testid, accent }) {
  return (
    <div className={`editorial-card-static p-6 ${accent ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : ""}`} data-testid={testid}>
      <div className="flex items-start justify-between mb-6">
        <div className={`overline ${accent ? "text-[#E63946]" : ""}`}>{label}</div>
        <Icon size={18} strokeWidth={1.5} />
      </div>
      <div className="font-display text-4xl font-bold tracking-tight">{value}</div>
    </div>
  );
}

function CourseCard({ course }) {
  return (
    <Link to={`/courses/${course.course_id}`} data-testid={`course-card-${course.course_id}`} className="editorial-card block">
      <img src={course.thumbnail} className="w-full h-44 object-cover" alt="" />
      <div className="p-5">
        <div className="flex items-center justify-between mb-2">
          <div className="overline">{course.category}</div>
          <div className="overline">{course.is_published ? "Published" : "Draft"}</div>
        </div>
        <div className="font-serif text-xl leading-tight mb-2">{course.title}</div>
        <div className="text-sm text-[#52525B] line-clamp-2 mb-4">{course.description}</div>
        <div className="flex items-center justify-between">
          <div className="text-sm font-display">{course.price > 0 ? `$${course.price}` : "Free"}</div>
          <ArrowUpRight size={16} strokeWidth={1.5} />
        </div>
      </div>
    </Link>
  );
}
